import EventDispatcher from '../../events/EventDispatcher';

/**
 * @class
 * @inheritDoc
 * @extends EventEmitter
 * @memberof module:media.module:loaders
 * @classdesc Очередь загрузки
 */
class Queue extends EventDispatcher {

    constructor(...items) {
        super();
        this.items = new Set();
        this.data = new Set();
        this._complete = false;
        this.add(...items);
    }

    /**
     * @type {module:media.module:loaders~ProgressInfo}
     * @readonly
     */
    get progressInfo() {
        let summary = { total: 0, complete: 0, progress: 0 };
        for (let item of this.items) {
            let { total, complete, progress } = item.progressInfo;
            summary.total += total;
            summary.complete += complete;
        }
        for (let item of this.data) {
            let { total, complete, progress } = item.progressInfo;
            summary.total += total;
            summary.complete += complete;
        }
        summary.progress = summary.total == 0 ? 1 : summary.complete / summary.total;
        return summary;
    }

    /**
     * @type {Boolean}
     * @readonly
     */
    get complete() {
        return this._complete;
    }

    //noinspection JSAnnotator
    set complete(val) {
        if (!this._complete) {
            this._complete = !!val;
            if (this._complete) {
                this._dispatchComplete();
            }
        }
    }

    /**
     * Добавить элементы для загрузки
     * @param {...module:media.module:loaders.QueueItem} items
     */
    add(...items) {
        for (let item of items) {
            item.once('complete', this._handleProgress, this);
            if (item instanceof Queue) {
                item.on('fileload', e => this._dispatchFileLoad(e.item));
                item.on('progress', this._handleProgress, this);
            }
            this.items.add(item);
        }
    }

    /**
     * Объединить очереди
     * @param {module:media.module:loaders.Queue} queue
     * @param {Boolean} [clear=true] Очистить переданную очередь
     */
    merge(queue, clear = true) {
        this.items = new Set([...this.items, ...queue.items]);
        this.data = new Set([...this.data, ...queue.data]);
        if (clear) {
            queue.clear();
        }
    }

    /**
     * Полная очистка очереди
     */
    clear() {
        this.items.clear();
        this.data.clear();
        this._complete = false;
    }

    /**
     * Начать загрузку
     * @param {Boolean} [cache=true] - Использовать кеш ресурсов
     */
    load(cache = true) {
        for (let item of this.items) {
            item.load(cache);
        }
        this._handleProgress();
    }

    _dispatchProgress() {
        /**
         * @event module:media.module:loaders.Queue#progress
         * @property {number} progress Completion progress 0..1
         * @property {number} complete Items completed
         * @property {number} total Items total
         */
        this.emit('progress', this.progressInfo);
    }

    _dispatchFileLoad(item) {
        /**
         * @event module:media.module:loaders.Queue#fileload
         * @property {QueueItem} item
         */
        this.emit('fileload', { item: item });
    }

    _dispatchComplete() {
        /**
         * @event module:media.module:loaders.Queue#complete
         * @property {*} data Loaded data (depends on loader type)
         */
        this.emit('complete');
    }

    _flushLoadedItems() {
        for (let item of this.items) {
            if (item.complete) {
                this.items.delete(item);
                this.data.add(item);
                if (!(item instanceof Queue)) {
                    this._dispatchFileLoad(item);
                }
            }
        }
    }

    _handleProgress() {
        this._flushLoadedItems();
        this._dispatchProgress();
        if (this.items.size == 0) {
            this.complete = true;
        }
    }
}

export default Queue;
